import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Copy, ExternalLink, SkipForward, Trash2, Volume2, X } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { useSocket } from "@/hooks/useSocket";
import { api } from "@/api/client";
export function SongRequestsPage() {
    const { activeChannel: channel } = useAuthStore();
    const { on, emit } = useSocket();
    const [settings, setSettings] = useState(null);
    const [queue, setQueue] = useState([]);
    const [nowPlaying, setNowPlaying] = useState(null);
    const [playerVolume, setPlayerVolume] = useState(80);
    const [copiedPlayer, setCopiedPlayer] = useState(false);
    useEffect(() => {
        if (channel) {
            loadSettings();
            loadQueue();
        }
    }, [channel]);
    useEffect(() => {
        const unsub1 = on("songrequest:added", (data) => {
            if (data.channelId === channel?.id) {
                setQueue((prev) => [...prev, data.request]);
            }
        });
        const unsub2 = on("songrequest:skipped", (data) => {
            if (data.channelId === channel?.id) {
                setQueue((prev) => prev.slice(1));
            }
        });
        const unsub3 = on("songrequest:queue", (data) => {
            if (data.channelId === channel?.id) {
                setQueue(data.queue);
            }
        });
        const unsub4 = on("songrequest:play", (data) => {
            if (data.channelId === channel?.id) {
                setNowPlaying(data.song);
            }
        });
        return () => { unsub1(); unsub2(); unsub3(); unsub4(); };
    }, [on, channel]);
    const loadSettings = async () => {
        if (!channel)
            return;
        const res = await api.get(`/channels/${channel.id}/songrequests/settings`);
        if (res.data)
            setSettings(res.data);
    };
    const loadQueue = async () => {
        if (!channel)
            return;
        const res = await api.get(`/channels/${channel.id}/songrequests/queue`);
        if (res.data)
            setQueue(res.data);
    };
    const update = async (data) => {
        if (!channel)
            return;
        const res = await api.patch(`/channels/${channel.id}/songrequests/settings`, data);
        if (res.data)
            setSettings(res.data);
    };
    const skip = async () => {
        if (!channel)
            return;
        await api.post(`/channels/${channel.id}/songrequests/skip`, {});
        loadQueue();
    };
    const remove = async (id) => {
        if (!channel)
            return;
        await api.delete(`/channels/${channel.id}/songrequests/queue/${id}`);
        loadQueue();
    };
    const clearQueue = async () => {
        if (!channel)
            return;
        await api.delete(`/channels/${channel.id}/songrequests/queue`);
        setQueue([]);
    };
    const playerUrl = channel ? `${window.location.origin}/overlay/${channel.overlayToken}/player` : "";
    const handleCopyPlayer = () => {
        navigator.clipboard.writeText(playerUrl);
        setCopiedPlayer(true);
        setTimeout(() => setCopiedPlayer(false), 2000);
    };
    const handleVolumeChange = (v) => {
        setPlayerVolume(v);
        if (channel) {
            emit("songrequest:volume", { channelId: channel.id, volume: v });
        }
    };
    if (!settings)
        return null;
    return (_jsxs("div", { className: "space-y-6", children: [_jsx("h1", { className: "text-2xl font-semibold", children: "Song Requests" }), _jsxs(Card, { children: [_jsx(CardHeader, { children: _jsx(CardTitle, { children: "Player Overlay" }) }), _jsxs(CardContent, { className: "space-y-4", children: [_jsx("p", { className: "text-sm text-muted-foreground", children: "Add this URL as a Browser Source in OBS to play song requests on stream." }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("code", { className: "flex-1 rounded-md border bg-muted p-3 text-sm break-all", children: playerUrl }), _jsxs(Button, { variant: "outline", size: "sm", onClick: handleCopyPlayer, children: [_jsx(Copy, { className: "mr-2 h-4 w-4" }), copiedPlayer ? "Copied!" : "Copy"] }), _jsx("a", { href: playerUrl, target: "_blank", rel: "noopener noreferrer", children: _jsxs(Button, { variant: "outline", size: "sm", children: [_jsx(ExternalLink, { className: "mr-2 h-4 w-4" }), " Preview"] }) })] }), nowPlaying && (_jsxs("div", { className: "flex items-center gap-3 rounded-md border bg-muted/50 p-3", children: [_jsx(Badge, { variant: "default", children: "Now Playing" }), _jsx("span", { className: "font-medium truncate flex-1", children: nowPlaying.title }), _jsxs("span", { className: "text-xs text-muted-foreground", children: ["by ", nowPlaying.requestedBy] })] })), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Volume2, { className: "h-4 w-4 text-muted-foreground" }), _jsxs(Label, { className: "w-24", children: ["Volume (", playerVolume, "%)"] }), _jsx("div", { className: "flex-1", children: _jsx(Slider, { value: playerVolume, onChange: handleVolumeChange }) })] })] })] }), _jsxs(Card, { children: [_jsxs(CardHeader, { className: "flex flex-row items-center justify-between pb-2", children: [_jsx(CardTitle, { children: "Settings" }), _jsx(Switch, { checked: settings.enabled, onCheckedChange: (checked) => update({ enabled: checked }) })] }), _jsx(CardContent, { className: "space-y-4", children: _jsxs("div", { className: "grid gap-4 md:grid-cols-3", children: [_jsxs("div", { children: [_jsx(Label, { children: "Max Queue Size" }), _jsx(Input, { type: "number", value: settings.maxQueueSize, onChange: (e) => update({ maxQueueSize: parseInt(e.target.value) }) })] }), _jsxs("div", { children: [_jsx(Label, { children: "Max Duration (sec)" }), _jsx(Input, { type: "number", value: settings.maxDurationSeconds, onChange: (e) => update({ maxDurationSeconds: parseInt(e.target.value) }) })] }), _jsxs("div", { children: [_jsx(Label, { children: "User Cooldown (sec)" }), _jsx(Input, { type: "number", value: settings.userCooldownSeconds, onChange: (e) => update({ userCooldownSeconds: parseInt(e.target.value) }) })] })] }) })] }), _jsxs(Card, { children: [_jsxs(CardHeader, { className: "flex flex-row items-center justify-between", children: [_jsxs(CardTitle, { children: ["Queue (", queue.length, ")"] }), _jsxs("div", { className: "flex gap-2", children: [_jsxs(Button, { variant: "outline", size: "sm", onClick: skip, disabled: queue.length === 0, children: [_jsx(SkipForward, { className: "mr-2 h-4 w-4" }), " Skip"] }), _jsxs(Button, { variant: "destructive", size: "sm", onClick: clearQueue, disabled: queue.length === 0, children: [_jsx(X, { className: "mr-2 h-4 w-4" }), " Clear"] })] })] }), _jsx(CardContent, { children: _jsxs("div", { className: "space-y-2", children: [queue.length === 0 && (_jsx("p", { className: "text-muted-foreground text-sm py-4 text-center", children: "Queue is empty. Users can request songs with !sr in chat." })), queue.map((song, i) => (_jsxs("div", { className: "flex items-center gap-3 text-sm border-b py-2", children: [_jsx("span", { className: "font-mono text-muted-foreground w-8", children: i === 0 ? (_jsx(Badge, { variant: "default", children: "Now" })) : (`#${i + 1}`) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "font-medium truncate", children: song.title }), song.url && (_jsx("a", { href: song.url, target: "_blank", rel: "noopener noreferrer", className: "text-xs text-brand hover:underline", children: song.url }))] }), _jsxs("span", { className: "text-xs text-muted-foreground shrink-0", children: ["by ", song.requestedBy] }), _jsx(Button, { size: "icon", variant: "ghost", "aria-label": `Song "${song.title}" aus Queue entfernen`, onClick: () => remove(song.id), children: _jsx(Trash2, { className: "h-3 w-3 text-destructive" }) })] }, song.id)))] }) })] })] }));
}
